import {Component, OnDestroy, QueryList, TemplateRef, ViewChild, ViewChildren, ChangeDetectionStrategy} from "@angular/core";
import {SortableDirective, SortEvent} from "@app/_directives/sortable.directive";
import {
    CreateWebDataConnectionSqlRequest,
    CreateWebDataConnectionThoughtSpotRequest,
    Customer,
    DeleteWebDataConnectionRequest, GetAllUsersResponse,
    RefreshWebDataConnectionRequest,
    UpdateWebDataConnectionSqlRequest,
    UpdateWebDataConnectionThoughtSpotRequest,
    User,
    WebDataConnection,
    WebDataConnectionApiKeyResponse,
    WebDataConnectionRun
} from "@app/_models/api";
import {BehaviorSubject, Observable, Subscription} from "rxjs";
import {GlobalService} from "@app/_services/global.service";
import {ApiService} from "@app/_services/api.service";
import {AsyncPipe, formatDate} from "@angular/common";
import {FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators} from "@angular/forms";
import {
    NgbDropdown,
    NgbDropdownAnchor,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbModal,
    NgbModalRef,
    NgbPagination
} from "@ng-bootstrap/ng-bootstrap";
import {ActivatedRoute} from "@angular/router";
import {ModalConfirmComponent} from "@app/_components/modal-confirm/modal-confirm.component";
import {ToastService} from "@app/_services/toast.service";
import {ClipboardService} from "@app/_services/clipboard.service";
import {WebDataConnectionService} from "@app/_services/datasources/web-data-connection.service";
import {environment} from "@environments/environment";
import {ThoughtspotService} from "@app/_services/thoughtspot.service";
import {Answer} from "@app/_models/thoughtspot";
import {WebDataConnectionRunsService} from "@app/_services/datasources/web-data-connection-runs.service";
import {TableSearchComponent} from "@app/_components/table-search/table-search.component";
import {TablePagerComponent} from "@app/_components/table-pager/table-pager.component";

interface WebDataConnectionForm {
    webDataConnectionId: FormControl<string | null>;
    customerId: FormControl<string | null>;
    name: FormControl<string | null>;
    description: FormControl<string | null>;
    sqlText: FormControl<string | null>;
    isActive: FormControl<boolean | null>;
    refreshHour: FormControl<number | null>;
    thoughtSpotAnswerId: FormControl<string | null>;
    thoughtSpotAnswerName: FormControl<string | null>;
    thoughtSpotUserName: FormControl<string | null>;
    type: FormControl<number | null>;
}

export enum WebDataConnectionType {
    Sql = 0,
    ThoughtSpotAnswerReport = 1
}

@Component({
    selector: "app-web-data-connections",
    standalone: true,
    imports: [
        TableSearchComponent,
        TablePagerComponent,
        AsyncPipe,
        FormsModule,
        NgbPagination,
        ReactiveFormsModule,
        SortableDirective,
        ModalConfirmComponent,
        NgbDropdown,
        NgbDropdownAnchor,
        NgbDropdownMenu,
        NgbDropdownItem
    ],
    templateUrl: "./web-data-connections.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./web-data-connections.component.scss"
})
export class WebDataConnectionsComponent implements OnDestroy {
    public WebDataConnectionType = WebDataConnectionType;
    public readonly typeOptions: number[] = [WebDataConnectionType.Sql, WebDataConnectionType.ThoughtSpotAnswerReport];
    @ViewChildren(SortableDirective) headers!: QueryList<SortableDirective>;
    @ViewChildren(SortableDirective) runsHeaders!: QueryList<SortableDirective>;
    @ViewChild("confirmationModal") modalConfirmComponent!: ModalConfirmComponent;
    customers: Customer[] = [];
    selectedCustomerId: string | undefined;
    selectedUserName: string | undefined;
    users: User[] = [];
    answers: Answer[] = [];
    webDataConnections: WebDataConnection[] = [];
    savingData: boolean = false;
    isEditMode: boolean = false;
    total$: Observable<number> | undefined;
    runsTotal$: Observable<number> | undefined;
    currentConnectionUrl: string | undefined;
    currentCustomerApiKey: string | undefined;
    webDataConnectionForm = new FormGroup<WebDataConnectionForm>({
        webDataConnectionId: new FormControl(null),
        customerId: new FormControl(null, Validators.required),
        name: new FormControl(null, Validators.required),
        description: new FormControl(null, Validators.required),
        sqlText: new FormControl(null, Validators.required),
        isActive: new FormControl(true, Validators.required),
        refreshHour: new FormControl(0, Validators.required),
        thoughtSpotAnswerId: new FormControl(null),
        thoughtSpotAnswerName: new FormControl(null),
        thoughtSpotUserName: new FormControl(null),
        type: new FormControl(1, Validators.required),
    });

    webDataConnectionRuns: WebDataConnectionRun[] = [];
    selectedRunsConnectionName: string | undefined;
    selectedWebDataConnectionId: string | undefined;
    protected readonly formatDate = formatDate;
    protected readonly parseInt = parseInt;
    @ViewChild("webDataConnectionModal") private webDataConnectionModal!: TemplateRef<ModalConfirmComponent>;
    @ViewChild("viewWebDataConnectionModal") private viewWebDataConnectionModal!: TemplateRef<ModalConfirmComponent>;
    @ViewChild("webDataConnectionRunsModal") private webDataConnectionRunsModal!: TemplateRef<ModalConfirmComponent>;
    private webDataConnectionModalRef!: NgbModalRef;
    private viewWebDataConnectionModalRef!: NgbModalRef;
    private webDataConnectionRunsModalRef!: NgbModalRef;
    private _customers$ = new BehaviorSubject<Customer[]>([]);
    private customersSubscription: Subscription;

    public constructor(
        public globalService: GlobalService,
        private apiService: ApiService,
        protected webDataConnectionService: WebDataConnectionService,
        protected webDataConnectionRunsService: WebDataConnectionRunsService,
        private thoughtSpotService: ThoughtspotService,
        private route: ActivatedRoute,
        private toastService: ToastService,
        private modalService: NgbModal,
        private clipboardService: ClipboardService
    ) {
        this._customers$.next(this.route.snapshot.data["resolvedData"]?.data || []);
        this.customersSubscription = this._customers$.subscribe(customers => {
            this.customers = customers;
        });
        this.total$ = this.webDataConnectionService.total$;
        this.runsTotal$ = this.webDataConnectionRunsService.total$;

        if (this.customers.length === 0) {
            this.loadCustomers();
        }

        this.controls.type.valueChanges.subscribe(v => this.updateValidatorsForType(v));
    }

    get controls() {
        return this.webDataConnectionForm.controls;
    }

    ngOnDestroy() {
        this.customersSubscription.unsubscribe();
    }

    loadCustomers() {
        this.globalService.showWaitOverlay();
        this.apiService.getCustomers().subscribe({
            next: (response: Customer[]) => {
                this._customers$.next(response);
                this.globalService.hideWaitOverlay();
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to load customers");
                this.globalService.hideWaitOverlay();
                console.error(error);
            }
        });
    }

    onCustomerChange(event: Event) {
        this.selectedCustomerId = (event.target as HTMLSelectElement).value;

        if (!this.selectedCustomerId) {
            return;
        }

        this.globalService.showWaitOverlay();
        this.loadWebDataConnections(this.selectedCustomerId);
        this.loadUsers(this.selectedCustomerId);
    }

    onUserChange(event: Event) {
        this.selectedUserName = (event.target as HTMLSelectElement).value;
        if (this.selectedCustomerId) {
            this.loadAnswers(this.selectedUserName).then();
        }
    }

    private loadWebDataConnections(customerId: string) {
        this.globalService.showWaitOverlay();
        this.apiService.getWebDataConnections(customerId).subscribe({
            next: (webDataConnections: WebDataConnection[]) => {
                this.webDataConnectionService.data = webDataConnections;
                this.webDataConnectionService.refreshDataSource();
                this.globalService.hideWaitOverlay();
            },
            error: (error) => {
                console.error(error);
                this.toastService.showDanger("Error", "Failed to load web data connections");
                this.globalService.hideWaitOverlay();
            }
        });

        this.apiService.getApiKey(customerId).subscribe({
            next: (response: WebDataConnectionApiKeyResponse) => {
                this.currentCustomerApiKey = response.apiKey;
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to fetch API key");
                this.globalService.hideWaitOverlay();
                console.error(error);
            }
        })
    }

    private loadUsers(customerId: string) {
        this.apiService.getUsers(customerId).subscribe({
            next: (response: GetAllUsersResponse) => {
                this.users = response.users;
            },
            error: (error) => {
                console.error(error);
                this.toastService.showDanger("Request Failed", "Failed to load users. Please try again.");
            }
        });
    }

    private async loadAnswers(username: string): Promise<void> {
        this.answers = [];
        const response = await this.thoughtSpotService.getAnswersMetadataForUser(username);
        this.answers = response.map(answer => (
            {id: answer.metadata_id!, name: answer.metadata_name!})
        );
    }

    onSort({column, direction}: SortEvent) {
        for (const header of this.headers) {
            if (header.sortable !== column) {
                header.direction = "";
            }
        }

        this.webDataConnectionService.sortColumn = column;
        this.webDataConnectionService.sortDirection = direction;
    }

    onSortRuns({column, direction}: SortEvent) {
        for (const header of this.runsHeaders) {
            if (header.sortable !== column) {
                header.direction = "";
            }
        }

        this.webDataConnectionRunsService.sortColumn = column;
        this.webDataConnectionRunsService.sortDirection = direction;
    }

    onAddWebDataConnection() {
        this.controls.type.enable();
        this.isEditMode = false;
        this.webDataConnectionForm.reset();
        this.controls.customerId.setValue(this.selectedCustomerId || null);
        this.updateValidatorsForType(this.controls.type.value);
        this.webDataConnectionModalRef = this.modalService.open(this.webDataConnectionModal, {
            modalDialogClass: "modal-dialog-centered modal-lg",
            backdrop: "static",
        });
    }

    onEditWebDataConnection(webDataConnection: WebDataConnection) {
        this.isEditMode = true;
        this.webDataConnectionForm.reset();
        this.controls.webDataConnectionId.setValue(webDataConnection.webDataConnectionId);
        this.controls.customerId.setValue(this.selectedCustomerId || null);
        this.controls.name.setValue(webDataConnection.name);
        this.controls.description.setValue(webDataConnection.description);
        this.controls.isActive.setValue(webDataConnection.isActive);
        this.controls.refreshHour.setValue(webDataConnection.refreshHour);
        this.controls.type.setValue(webDataConnection.type);
        this.controls.type.disable();

        switch (webDataConnection.type) {
            case WebDataConnectionType.Sql:
                this.controls.sqlText.setValue(webDataConnection.sqlConfig.sqlText ?? null);
                break;
            case WebDataConnectionType.ThoughtSpotAnswerReport:
                this.controls.thoughtSpotUserName.setValue(webDataConnection.thoughtSpotConfig.userName ?? null);
                break;
        }

        this.updateValidatorsForType(this.controls.type.value);

        this.webDataConnectionModalRef = this.modalService.open(this.webDataConnectionModal, {
            modalDialogClass: "modal-dialog-centered modal-lg",
            backdrop: "static",
        });

        if (webDataConnection.type == WebDataConnectionType.ThoughtSpotAnswerReport) {
            let thoughtSpotConfig = webDataConnection.thoughtSpotConfig;
            this.loadAnswers(thoughtSpotConfig.userName).then(() => {
                this.controls.thoughtSpotAnswerId.setValue(thoughtSpotConfig.answerId ?? null);
                this.controls.thoughtSpotAnswerName.setValue(thoughtSpotConfig.answerName ?? null);
            });
        } else {
            this.answers = [];
            this.controls.thoughtSpotAnswerId.setValue(null);
            this.controls.thoughtSpotAnswerName.setValue(null);
        }
    }

    onViewWebDataConnection(webDataConnection: WebDataConnection) {
        this.webDataConnectionForm.reset();
        this.controls.webDataConnectionId.setValue(webDataConnection.webDataConnectionId);
        this.controls.customerId.setValue(this.selectedCustomerId || null);
        this.controls.name.setValue(webDataConnection.name);
        this.controls.description.setValue(webDataConnection.description);
        this.controls.isActive.setValue(webDataConnection.isActive);
        this.controls.refreshHour.setValue(webDataConnection.refreshHour);

        this.currentConnectionUrl = `${environment.apiUrl}/${webDataConnection.url}`;

        switch (webDataConnection.type) {
            case WebDataConnectionType.Sql:
                this.controls.sqlText.setValue(webDataConnection.sqlConfig.sqlText ?? null);
                break;
            case WebDataConnectionType.ThoughtSpotAnswerReport:
                this.controls.thoughtSpotUserName.setValue(webDataConnection.thoughtSpotConfig.userName ?? null);
                break;
        }

        this.controls.type.setValue(webDataConnection.type);

        this.updateValidatorsForType(this.controls.type.value);

        if (webDataConnection.type == WebDataConnectionType.ThoughtSpotAnswerReport) {
            let thoughtSpotConfig = webDataConnection.thoughtSpotConfig;
            this.loadAnswers(thoughtSpotConfig.userName).then(() => {
                this.controls.thoughtSpotAnswerId.setValue(thoughtSpotConfig.answerId ?? null);
                this.controls.thoughtSpotAnswerName.setValue(thoughtSpotConfig.answerName ?? null);
            });
        } else {
            this.answers = [];
            this.controls.thoughtSpotAnswerId.setValue(null);
            this.controls.thoughtSpotAnswerName.setValue(null);
        }

        this.webDataConnectionForm.disable();

        this.viewWebDataConnectionModalRef = this.modalService.open(this.viewWebDataConnectionModal, {
            modalDialogClass: "modal-dialog-centered modal-lg",
            backdrop: "static",
        });
    }

    onWebDataConnectionDismiss() {
        this.modalConfirmComponent.confirmIfDirty(this.webDataConnectionForm, () => this.webDataConnectionModalRef.dismiss());
    }

    onViewWebDataConnectionDismiss() {
        this.viewWebDataConnectionModalRef.dismiss();
        this.webDataConnectionForm.enable();
    }

    onViewWebDataConnectionRunsDismiss() {
        this.webDataConnectionRunsModalRef.dismiss();
    }

    onWebDataConnectionSubmit() {
        if (this.webDataConnectionForm.invalid) {
            this.toastService.showDanger("Invalid Data", "Please fill in all required fields.");
            return;
        }

        this.savingData = true;

        const formValue = this.webDataConnectionForm.getRawValue();
        const typeNum = (typeof formValue.type) === "string" ? parseInt(formValue.type, 10) : formValue.type;

        switch (typeNum) {
            case WebDataConnectionType.Sql:
                if (this.isEditMode) {
                    this.handleEditSaveSql(formValue);
                } else {
                    this.handleCreateSaveSql(formValue);
                }
                break;
            case WebDataConnectionType.ThoughtSpotAnswerReport:
                let resolvedAnswerName =
                    this.answers.find(a => a.id == formValue.thoughtSpotAnswerId)?.name!;

                if (this.isEditMode) {
                    this.handleEditSaveThoughtSpot(formValue, resolvedAnswerName);
                } else {
                    this.handleCreateSaveThoughtSpot(formValue, resolvedAnswerName);
                }
                break;
            default:
                console.error("Unknown WebDataConnectionType: " + formValue.type);
                this.toastService.showDanger("Invalid Data", "Unknown web data connection type");
                this.savingData = false;
        }
    }

    onViewWebDataConnectionRuns(webDataConnection: WebDataConnection) {
        this.selectedRunsConnectionName = webDataConnection.name;
        this.selectedWebDataConnectionId = webDataConnection.webDataConnectionId;
        this.webDataConnectionRuns = [];
        this.globalService.showWaitOverlay("Loading runs...");

        this.apiService.getWebDataConnectionRuns(this.selectedCustomerId!, webDataConnection.webDataConnectionId).subscribe({
            next: (webDataConnectionRuns: WebDataConnectionRun[]) => {
                this.webDataConnectionRunsService.data = webDataConnectionRuns;
                this.webDataConnectionRunsService.refreshDataSource();
                this.globalService.hideWaitOverlay();

                this.webDataConnectionRunsModalRef = this.modalService.open(this.webDataConnectionRunsModal, {
                    modalDialogClass: "modal-dialog-centered modal-lg",
                    backdrop: "static",
                });
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to load runs for this web data connection");
                console.error(error);
                this.globalService.hideWaitOverlay();
            }
        });
    }

    onDownloadFile(customerId: string, webDataConnectionId: string, fileName: string, versionId: string): void {
        this.webDataConnectionRunsService.downloadRawFile(customerId, webDataConnectionId, fileName, versionId);
    }

    onRefreshWebDataConnection(webDataConnection: WebDataConnection) {
        if (!this.selectedCustomerId) {
            this.toastService.showDanger("Error", "Please select a customer first.");
            return;
        }
        const request: RefreshWebDataConnectionRequest = {
            customerId: this.selectedCustomerId,
            webDataConnectionId: webDataConnection.webDataConnectionId
        };

        this.globalService.showWaitOverlay("Please wait...");
        this.apiService.refreshWebDataConnection(request).subscribe({
            next: (response) => {
                const msg = (response as any)?.message ?? "Refresh started successfully";
                this.toastService.showSuccess("Success", msg);
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to refresh web data connection");
                this.globalService.hideWaitOverlay();
                console.error(error);
            },
            complete: () => {
                this.globalService.hideWaitOverlay();
            }
        });
    }

    onDeleteWebDataConnection(webDataConnection: WebDataConnection) {
        if (!this.selectedCustomerId) {
            this.toastService.showDanger("Error", "Please select a customer first.");
            return;
        }

        this.modalConfirmComponent.open(
            "Delete Web Data Connection",
            `Are you sure you want to delete the web data connection '${webDataConnection.name}'?`);

        this.modalConfirmComponent.modalRef.result.then(
            () => {
                this.globalService.showWaitOverlay("Deleting...");

                const request: DeleteWebDataConnectionRequest = {
                    customerId: this.selectedCustomerId!,
                    webDataConnectionId: webDataConnection.webDataConnectionId
                };
                this.apiService.deleteWebDataConnection(request).subscribe({
                    next: (response) => {
                        this.toastService.showSuccess("Success", response.message || "Web data connection deleted successfully");
                        this.loadWebDataConnections(this.selectedCustomerId!);
                    },
                    error: (error) => {
                        this.toastService.showDanger("Error", "Failed to delete web data connection");
                        this.globalService.hideWaitOverlay();
                        console.error(error);
                    },
                    complete: () => {
                        this.globalService.hideWaitOverlay();
                    }
                });
            },
            () => {
            }
        );
    }

    copyUrlToClipboard() {
        if (this.currentConnectionUrl) {
            this.clipboardService.copyToClipboard(this.currentConnectionUrl, "URL");
        }
    }

    copyApiKeyToClipboard() {
        if (this.currentCustomerApiKey) {
            this.clipboardService.copyToClipboard(this.currentCustomerApiKey, "API Key");
        }
    }

    longFormatDate(input: string): string {
        if (!input) {
            return "";
        }
        return this.formatDate(input + (input.endsWith("Z") ? "" : "Z"), "yyyy-MM-dd, hh:mm a", "en-US")
    }

    getWebDataConnectionTypeLabel(type: number | null): string {
        switch (type || 0) {
            case WebDataConnectionType.Sql:
                return "SQL";
            case WebDataConnectionType.ThoughtSpotAnswerReport:
                return "TS Answer Report";
            default:
                return "Unknown";
        }
    }

    private handleCreateSaveSql(formValue: any) {
        const createRequest: CreateWebDataConnectionSqlRequest = {
            name: formValue.name!,
            description: formValue.description!,
            isActive: formValue.isActive!,
            refreshHour: formValue.refreshHour!,
            sqlText: formValue.sqlText?.trim()
        } as CreateWebDataConnectionSqlRequest;

        this.globalService.showWaitOverlay();
        this.apiService.createWebDataConnectionSql(formValue.customerId!, createRequest).subscribe({
            next: () => {
                this.toastService.showSuccess("Success", "Web data connection created successfully");
                if (this.selectedCustomerId) {
                    this.loadWebDataConnections(this.selectedCustomerId);
                }
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to create web data connection");
                this.savingData = false;
                console.error(error);
            },
            complete: () => {
                this.savingData = false;
                this.webDataConnectionModalRef.close();
            }
        });

        this.globalService.hideWaitOverlay();
    }

    private handleCreateSaveThoughtSpot(formValue: any, resolvedAnswerName: string) {
        const createRequest: CreateWebDataConnectionThoughtSpotRequest = {
            name: formValue.name!,
            description: formValue.description!,
            isActive: formValue.isActive!,
            refreshHour: formValue.refreshHour!,
            answerId: formValue.thoughtSpotAnswerId,
            answerName: resolvedAnswerName,
            userName: formValue.thoughtSpotUserName
        } as CreateWebDataConnectionThoughtSpotRequest;

        this.globalService.showWaitOverlay();
        this.apiService.createWebDataConnectionThoughtSpot(formValue.customerId!, createRequest).subscribe({
            next: () => {
                this.toastService.showSuccess("Success", "Web data connection created successfully");
                if (this.selectedCustomerId) {
                    this.loadWebDataConnections(this.selectedCustomerId);
                }
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to create web data connection");
                this.savingData = false;
                console.error(error);
            },
            complete: () => {
                this.savingData = false;
                this.webDataConnectionModalRef.close();
            }
        });
        this.globalService.hideWaitOverlay();
    }

    private handleEditSaveSql(formValue: any) {
        const updateRequest: UpdateWebDataConnectionSqlRequest = {
            name: formValue.name!,
            description: formValue.description!,
            isActive: formValue.isActive!,
            refreshHour: formValue.refreshHour!,
            sqlText: formValue.sqlText?.trim(),
        } as UpdateWebDataConnectionSqlRequest;

        this.globalService.showWaitOverlay();
        this.apiService.updateWebDataConnectionSql(formValue.customerId!, formValue.webDataConnectionId!, updateRequest).subscribe({
            next: () => {
                this.toastService.showSuccess("Success", "Web data connection updated successfully");
                if (this.selectedCustomerId) {
                    this.loadWebDataConnections(this.selectedCustomerId);
                }
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to update web data connection");
                this.savingData = false;
                console.error(error);
            },
            complete: () => {
                this.savingData = false;
                this.webDataConnectionModalRef.close();
            }
        });

        this.globalService.hideWaitOverlay();
    }

    private handleEditSaveThoughtSpot(formValue: any, resolvedAnswerName: string | null) {
        const updateRequest: UpdateWebDataConnectionThoughtSpotRequest = {
            name: formValue.name!,
            description: formValue.description!,
            isActive: formValue.isActive!,
            refreshHour: formValue.refreshHour!,
            answerId: formValue.thoughtSpotAnswerId,
            answerName: resolvedAnswerName,
            userName: formValue.thoughtSpotUserName
        } as UpdateWebDataConnectionThoughtSpotRequest;

        this.globalService.showWaitOverlay();
        this.apiService.updateWebDataConnectionThoughtSpot(formValue.customerId!, formValue.webDataConnectionId!, updateRequest).subscribe({
            next: () => {
                this.toastService.showSuccess("Success", "Web data connection updated successfully");
                if (this.selectedCustomerId) {
                    this.loadWebDataConnections(this.selectedCustomerId);
                }
            },
            error: (error) => {
                this.toastService.showDanger("Error", "Failed to update web data connection");
                this.savingData = false;
                console.error(error);
            },
            complete: () => {
                this.savingData = false;
                this.webDataConnectionModalRef.close();
            }
        });

        this.globalService.hideWaitOverlay();
    }

    private updateValidatorsForType(typeVal: any) {
        const typeNum = typeof typeVal === "string" ? parseInt(typeVal, 10) : typeVal;

        if (typeNum === WebDataConnectionType.Sql) {
            this.controls.sqlText.addValidators([Validators.required]);
            this.controls.thoughtSpotAnswerId.clearValidators();
            this.controls.thoughtSpotUserName.clearValidators();
        } else if (typeNum === WebDataConnectionType.ThoughtSpotAnswerReport) {
            this.controls.sqlText.clearValidators();
            this.controls.thoughtSpotAnswerId.addValidators([Validators.required]);
            this.controls.thoughtSpotUserName.addValidators([Validators.required]);
        } else {
            this.controls.sqlText.clearValidators();
            this.controls.thoughtSpotAnswerId.clearValidators();
            this.controls.thoughtSpotUserName.clearValidators();
        }
        this.controls.sqlText.updateValueAndValidity({onlySelf: true});
        this.controls.thoughtSpotAnswerId.updateValueAndValidity({onlySelf: true});
        this.controls.thoughtSpotUserName.updateValueAndValidity({onlySelf: true});
    }

    protected readonly length = length;
}
